import React from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, Play, Edit, Upload, CheckCircle,
    Clock, AlertCircle, RefreshCw, ExternalLink
} from 'lucide-react';

/**
 * ContentTypeCard - Individual content type card for the repurpose grid
 * 
 * Design matches the Pixar studio aesthetic:
 * - White cards with rounded-[24px] corners
 * - Shadow-xl with indigo tint
 * - Status-based visual indicators
 * - Aspect-ratio-correct preview areas
 */
const ContentTypeCard = ({
    contentType,
    status = 'empty', // 'empty' | 'generating' | 'generated' | 'published'
    onGenerate,
    onEdit,
    onPublish,
    onPreview
}) => {
    const {
        id,
        name,
        platform,
        aspectRatio,
        icon: Icon,
        iconColor = 'text-indigo-600',
        iconBg = 'bg-indigo-100',
        description,
        duration,
        generatedAt,
        PreviewComponent
    } = contentType;

    // Status-based styling
    const statusConfig = {
        empty: {
            border: 'border-slate-200',
            bg: 'bg-white',
            indicator: null,
            label: 'Not Generated',
            labelColor: 'text-slate-400'
        },
        generating: {
            border: 'border-indigo-200',
            bg: 'bg-white',
            indicator: { icon: RefreshCw, color: 'text-indigo-500', animate: true },
            label: 'Generating...',
            labelColor: 'text-indigo-600'
        },
        generated: {
            border: 'border-green-200',
            bg: 'bg-white',
            indicator: { icon: CheckCircle, color: 'text-green-500', animate: false },
            label: 'Ready',
            labelColor: 'text-green-600'
        },
        published: {
            border: 'border-purple-200',
            bg: 'bg-white',
            indicator: { icon: CheckCircle, color: 'text-purple-500', animate: false },
            label: 'Published',
            labelColor: 'text-purple-600'
        }
    };

    const config = statusConfig[status];

    // Aspect ratio to Tailwind class mapping
    const aspectClass = {
        '9:16': 'aspect-[9/16]',
        '16:9': 'aspect-video',
        '1:1': 'aspect-square',
        '4:5': 'aspect-[4/5]'
    }[aspectRatio] || 'aspect-square';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`
                relative bg-white rounded-[24px] p-1 shadow-xl shadow-indigo-100/50 
                border ${config.border} group hover:-translate-y-1 transition-transform duration-300
            `}
        >
            {/* Status Badge */}
            {status !== 'empty' && config.indicator && (
                <div className={`
                    absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg z-10
                    ${status === 'generated' ? 'bg-green-500' : status === 'published' ? 'bg-purple-500' : 'bg-indigo-500'}
                `}>
                    <config.indicator.icon
                        size={14}
                        className={`text-white ${config.indicator.animate ? 'animate-spin' : ''}`}
                    />
                </div>
            )}

            <div className="p-3">
                {/* Preview Area */}
                <div className={`
                    ${aspectClass} bg-gradient-to-br from-slate-100 to-slate-50 
                    rounded-[20px] mb-3 overflow-hidden relative
                    flex items-center justify-center
                `}>
                    {status === 'empty' ? (
                        // Empty State
                        <div className="flex flex-col items-center justify-center text-center p-4">
                            <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center mb-2`}>
                                <Icon size={24} className={iconColor} />
                            </div>
                            <p className="text-xs text-slate-400 font-medium">Preview</p>
                        </div>
                    ) : status === 'generating' ? (
                        // Generating State
                        <div className="flex flex-col items-center justify-center text-center p-4">
                            <RefreshCw size={32} className="text-indigo-400 animate-spin mb-2" />
                            <p className="text-xs text-indigo-500 font-medium">Creating...</p>
                        </div>
                    ) : (
                        // Generated Content Preview
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center relative group">
                            {/* Placeholder for actual preview */}
                            {PreviewComponent ? (
                                <PreviewComponent />
                            ) : (
                                <div className={`w-full h-full ${iconBg} flex items-center justify-center`}>
                                    <Icon size={40} className={`${iconColor} opacity-30`} />
                                </div>
                            )}

                            {/* Play/Preview Overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <button
                                    onClick={onPreview}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg"
                                >
                                    <Play size={20} className="text-slate-700 ml-1" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Content Info */}
                <div className="px-1">
                    {/* Platform Badge */}
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                            {platform}
                        </span>
                        <span className="text-[10px] text-slate-400">
                            {aspectRatio}
                        </span>
                        {duration && (
                            <span className="text-[10px] text-slate-400">
                                {duration}
                            </span>
                        )}
                    </div>

                    {/* Name */}
                    <h3 className="font-bold text-slate-900 font-['Outfit'] text-sm leading-tight mb-1">
                        {name}
                    </h3>

                    {/* Status Label */}
                    <p className={`text-xs font-medium ${config.labelColor} mb-3`}>
                        {config.label}
                        {generatedAt && ` · ${generatedAt}`}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        {status === 'empty' && (
                            <button
                                onClick={onGenerate}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                    bg-indigo-600 text-white rounded-xl text-xs font-bold 
                                    hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all"
                            >
                                <Sparkles size={12} />
                                <span>Generate</span>
                            </button>
                        )}

                        {status === 'generating' && (
                            <button
                                onClick={platform === 'TikTok' ? onPreview : undefined}
                                disabled={platform !== 'TikTok'}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                    rounded-xl text-xs font-bold transition-all
                                    ${platform === 'TikTok'
                                        ? 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 cursor-pointer shadow-sm border border-indigo-100'
                                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                            >
                                <RefreshCw size={12} className="animate-spin" />
                                <span>Processing</span>
                            </button>
                        )}

                        {status === 'generated' && (
                            <>
                                <button
                                    onClick={onEdit}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                        bg-slate-100 text-slate-600 rounded-xl text-xs font-bold 
                                        hover:bg-slate-200 transition-all"
                                >
                                    <Edit size={12} />
                                    <span>Edit</span>
                                </button>
                                <button
                                    onClick={onPublish}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                        bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl 
                                        text-xs font-bold hover:shadow-lg transition-all"
                                >
                                    <Upload size={12} />
                                    <span>Publish</span>
                                </button>
                            </>
                        )}

                        {status === 'published' && (
                            <button
                                onClick={onPreview}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                    bg-purple-100 text-purple-600 rounded-xl text-xs font-bold 
                                    hover:bg-purple-200 transition-all"
                            >
                                <ExternalLink size={12} />
                                <span>View Live</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ContentTypeCard;